import { captureMessage } from "@sentry/react";
import { useState } from "react";
import { TbDeviceMobilePin, TbSearch } from "react-icons/tb";
import { Controller, useForm } from "react-hook-form";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { Alert, Box, Button, Stack, TextInput, rem } from "@mantine/core";
import type { InferInput } from "valibot";
import { minLength, object, pipe, string } from "valibot";

import { LookupResult } from "./lookup-result";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// IMSI Lookup Form Values Schema
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const ImsiLookupFormValuesSchema = object({
  imsi: pipe(string(), minLength(1, "Please enter IMSI")),
});

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// IMSI Lookup Form Values
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

type ImsiLookupFormValues = InferInput<typeof ImsiLookupFormValuesSchema>;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// IMSI Lookup Form
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

type ImsiLookupFormProps = {
  readonly apiKey: string;
  readonly defaultValues?: Partial<ImsiLookupFormValues>;
};

export function ImsiLookupForm({ apiKey, defaultValues }: ImsiLookupFormProps) {
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<object | null>(null);

  const { handleSubmit, formState, control } = useForm<ImsiLookupFormValues>({
    resolver: valibotResolver(ImsiLookupFormValuesSchema),
    defaultValues: {
      imsi: defaultValues?.imsi ?? "",
    },
  });

  const { isSubmitting, submitCount, isValid, errors } = formState;

  const isFormInvalid = Object.keys(errors).length !== 0 || !isValid;
  const isSubmitDisabled = (submitCount > 0 && isFormInvalid) || isSubmitting;

  // TODO: THIS IS NOT THE BEST WAY TO CHECK FOR INVALID IMSI
  // const isInvalidImsi = lookupResult && false;

  return (
    <Stack gap="xl" align="center" w="100%">
      <Box w="100%" miw={rem(380)} maw={rem(480)}>
        <Stack gap="xl" w="100%">
          <Box
            component="form"
            noValidate={true}
            onSubmit={handleSubmit(async (values) => {
              setLookupError(null);

              try {
                const apiUrl = new URL(__IMSI_LOOKUP_API_URL__);
                apiUrl.searchParams.set("key", apiKey);
                apiUrl.searchParams.set("imsi", values.imsi);

                const response = await fetch(apiUrl.toString());

                if (response.status === 401) {
                  return setLookupError("Invalid API Key!");
                }

                if (!response.ok) {
                  captureMessage(
                    `IMSI Lookup API Response Error: ${response.statusText}`,
                    { level: "fatal" },
                  );

                  return setLookupError(response.statusText);
                }

                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                return setLookupResult(await response.json());
              } catch (error) {
                if (error instanceof Error) {
                  captureMessage(
                    `IMSI Lookup Request Error: ${error.message}`,
                    { level: "fatal" },
                  );

                  return setLookupError(error.message);
                }

                captureMessage("IMSI Lookup Request Error: Unknown Error", {
                  level: "fatal",
                });

                return setLookupError("Something went wrong!");
              }
            })}
          >
            <Stack>
              <Controller
                control={control}
                name="imsi"
                render={({ field, fieldState: { error } }) => (
                  <TextInput
                    {...field}
                    leftSection={<TbDeviceMobilePin />}
                    label="IMSI"
                    description="Enter IMSI you want to lookup"
                    placeholder="Enter IMSI you want to lookup"
                    error={error?.message}
                    required={true}
                    readOnly={isSubmitting}
                  />
                )}
              />

              <Button
                type="submit"
                fullWidth={true}
                leftSection={<TbSearch />}
                disabled={isSubmitDisabled}
                loading={isSubmitting}
              >
                IMSI Lookup
              </Button>
            </Stack>
          </Box>

          {/* {isInvalidImsi && (
            <Alert variant="filled" color="red" ta="center" fw="bold" w="100%">
              Invalid Number
            </Alert>
          )} */}

          {/* {!isInvalidImsi && lookupError && ( */}
          {lookupError && (
            <Alert variant="filled" color="red" ta="center" fw="bold" w="100%">
              {lookupError}
            </Alert>
          )}
        </Stack>
      </Box>

      {/* {!isInvalidImsi && !lookupError && lookupResult && ( */}
      {!lookupError && lookupResult && (
        <LookupResult isLoading={isSubmitting} lookupResult={lookupResult} />
      )}
    </Stack>
  );
}
